import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl, isProductionEnvironment } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

const COLUMN_ORDER = [
  'id',
  'property_address',
  'referral_side', 'keyflo_agent_role', 'user_id',
  'contract_id', 'listing_id', 'buyer_agreement_id',
  'expected_referral_fee_amount', 'purchase_price',
  'referring_agent_name', 'referring_agent_email', 'referring_agent_phone',
  'referring_brokerage_name', 'receiving_agent_name', 'receiving_agent_email',
  'receiving_brokerage_name', 'is_internal_referral',
  'referral_fee_percent', 'referral_fee_amount', 'referral_fee_type',
  'skyslope_sale_guid', 'skyslope_checklist_type_id',
  'w9_received', 'skyslope_sync_status', 'source_split_id', 'created_at',
];

function DeveloperReferralAgreements() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contractId, setContractId] = useState('');
  const [listingId, setListingId] = useState('');
  const [buyerAgreementId, setBuyerAgreementId] = useState('');
  const [referralSide, setReferralSide] = useState('');
  const [userId, setUserId] = useState('');
  const [keyfloAgentRole, setKeyfloAgentRole] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isProd = isProductionEnvironment();
  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'].includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const fetchRows = async () => {
    if (!isAuthenticated || !isKeyFloUser || !user?.email) return;
    try {
      setLoading(true);
      setError('');
      const params = { limit: 100, offset: 0, user_email: user.email };
      if (contractId) params.contract_id = contractId;
      if (listingId) params.listing_id = listingId;
      if (buyerAgreementId) params.buyer_agreement_id = buyerAgreementId;
      if (referralSide) params.referral_side = referralSide;
      if (userId) params.user_id = userId;
      if (keyfloAgentRole) params.keyflo_agent_role = keyfloAgentRole;
      const res = await axios.get(`${BACKEND_URL}/dev/referral-agreements`, { params });
      setRows(res.data?.referral_agreements || []);
    } catch (e) {
      setError(e.message || 'Failed to load referral agreements');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    const observed = new Set(['Actions', 'id', 'property_address']);
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => observed.add(k)));
    const ordered = ['Actions', ...COLUMN_ORDER.filter((c) => observed.has(c))];
    observed.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  }, [rows]);

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser]);

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
      }
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr || '';
    }
  };

  const formatCellValue = (col, val) => {
    if (val == null) return '';
    if (col.endsWith('_at')) return formatDateTime(val);
    if (col.endsWith('_date')) return formatDate(val);
    return String(val);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setDeleting(true);
      await axios.delete(`${BACKEND_URL}/referral-agreements/${rowToDelete.id}/hard`, {
        params: { caller_email: user?.email },
      });
      setRows((prev) => prev.filter((r) => r.id !== rowToDelete.id));
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setRowToDelete(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '24px' }}>
        <button type="button" onClick={() => loginWithRedirect()}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return <div style={{ padding: '24px' }}><h2>Access Denied</h2></div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Referral Agreements</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button type="button" className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '12px' }}>
        <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" />
        <input value={listingId} onChange={(e) => setListingId(e.target.value)} placeholder="listing_id" />
        <input value={buyerAgreementId} onChange={(e) => setBuyerAgreementId(e.target.value)} placeholder="buyer_agreement_id" />
        <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user_id" />
        <select value={keyfloAgentRole} onChange={(e) => setKeyfloAgentRole(e.target.value)}>
          <option value="">any role</option>
          <option value="referring">referring</option>
          <option value="receiving">receiving</option>
        </select>
        <select value={referralSide} onChange={(e) => setReferralSide(e.target.value)}>
          <option value="">any side</option>
          <option value="buyer">buyer</option>
          <option value="seller">seller</option>
        </select>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <button type="button" onClick={fetchRows}>Apply Filters</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div style={{ overflowX: 'auto', maxHeight: '75vh' }}>
        <table style={{ width: 'max-content', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '8px',
                    borderBottom: '1px solid #ccc',
                    textAlign: 'left',
                    position: ['Actions', 'id', 'property_address'].includes(col) ? 'sticky' : 'static',
                    left: col === 'Actions' ? 0 : col === 'id' ? 90 : col === 'property_address' ? 150 : undefined,
                    background: ['Actions', 'id', 'property_address'].includes(col) ? '#fff' : undefined,
                    zIndex: ['Actions', 'id', 'property_address'].includes(col) ? 2 : 1,
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((col) => (
                  <td
                    key={col}
                    style={{
                      padding: '8px',
                      borderBottom: '1px solid #eee',
                      position: ['Actions', 'id', 'property_address'].includes(col) ? 'sticky' : 'static',
                      left: col === 'Actions' ? 0 : col === 'id' ? 90 : col === 'property_address' ? 150 : undefined,
                      background: ['Actions', 'id', 'property_address'].includes(col) ? '#fff' : undefined,
                      minWidth: col === 'Actions' ? 90 : undefined,
                    }}
                  >
                    {col === 'Actions' ? (
                      <button type="button" onClick={() => { setRowToDelete(row); setShowDeleteModal(true); }}>Delete</button>
                    ) : (
                      formatCellValue(col, row[col])
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', padding: 24, borderRadius: 8, maxWidth: 420 }}>
            <h3>Delete referral agreement #{rowToDelete?.id}?</h3>
            {isProd && <p style={{ color: 'crimson' }}>Production environment — this is permanent.</p>}
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button type="button" disabled={deleting} onClick={confirmDelete}>{deleting ? 'Deleting…' : 'Confirm'}</button>
              <button type="button" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperReferralAgreements;
